import { apiError } from '../apiError';
import axiosInstance from '../axios.config';
import { ApiResponse } from 'types';

/**
 * TRAI TCCCPR compliance — SHADOW ONLY on the server.
 *
 * Backend: core-service `/api/v1/compliance/**` (branch feat/trai-compliance-shadow). Nothing here
 * refuses a call: categories feed the existing A2P check (shadow in production), declarations are
 * reported, consent is looked up after a call and recorded as "would refuse", complaint thresholds
 * are computed and alerts stored (never emailed).
 *
 * Contract notes (the known trap): the API is camelCase, and lists come back either bare or as
 * `data.content` — each reader below names which. Failures arrive as HTTP 200 with
 * `{ success: false, message }`, so `error` is filled from `message`.
 */

export type A2pCategory = 'PROMOTIONAL' | 'SERVICE';

export const CATEGORY_OPTIONS: ReadonlyArray<readonly ['' | A2pCategory, string]> = [
  ['', 'Not set (today\'s behaviour)'],
  ['PROMOTIONAL', 'Promotional'],
  ['SERVICE', 'Service / transactional'],
];

export interface CategoryDefaults {
  organizationId: string;
  orgDefault: A2pCategory | null;
  /** campaign id → category */
  campaigns: Record<string, A2pCategory>;
  /** cpass agent id → category */
  agents: Record<string, A2pCategory>;
  resolutionOrder: string[];
}

export interface DeclaredNumber {
  id: number;
  e164: string;
  status: string | null;
  enabled: boolean | null;
  businessId: string | null;
  organizationId: string | null;
  declaredAt: string | null;
  declaredRef: string | null;
  category: A2pCategory | null;
  regime: string | null;
  effectiveRegime: 'TRAI_IN' | 'NONE';
  /** PROMOTIONAL_140 | SERVICE_1600 | SERVICE_1601 | ORDINARY, null for a non-Indian number. */
  series: string | null;
  declared: boolean;
  warnings: string[];
}

export interface DeclarationReportRow {
  cli: string;
  numberId: number | null;
  calls: number;
  callsByCategory: Record<string, number>;
  sources: string[];
  regime: string;
  series: string | null;
  declared: boolean;
  declaredCategory: A2pCategory | null;
  declaredRef: string | null;
  issues: string[];
}

export interface DeclarationReport {
  organizationId: string;
  since: string;
  days: number;
  auditStartsAt: string | null;
  numbersUsed: number;
  numbersWithIssues: number;
  a2pGateEnforcing: boolean;
  rows: DeclarationReportRow[];
}

export interface ImportResult {
  dryRun: boolean;
  rows: number;
  valid: number;
  invalid: number;
  written: number;
  /** Declarations: one entry per row. */
  results?: Array<Record<string, any>>;
  /** Consent / complaints: rejected rows. */
  errors?: Array<{ line: number; error: string; [k: string]: any }>;
  /** Consent / complaints: the first valid rows, as they would be stored. */
  preview?: Array<Record<string, any>>;
  creates?: number;
  updates?: number;
}

export type ConsentSource = 'WEB_FORM' | 'IVR' | 'WHATSAPP_OPTIN' | 'CRM_IMPORT' | 'API' | 'VERBAL_ON_CALL' | 'OTHER';
export const CONSENT_SOURCES: ConsentSource[] = ['WEB_FORM', 'IVR', 'WHATSAPP_OPTIN', 'CRM_IMPORT', 'API', 'VERBAL_ON_CALL', 'OTHER'];
export type ConsentScope = 'PROMOTIONAL' | 'SERVICE' | 'ALL';

export interface ConsentRecord {
  id: number;
  /** Last four digits only — the full number is never stored. */
  number: string;
  source: ConsentSource;
  scope: ConsentScope;
  capturedAt: string;
  capturedBy: string | null;
  evidenceRef: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  revokedBy: string | null;
  revokeReason: string | null;
  active: boolean;
}

export interface ConsentLookup {
  number: string;
  activePromotional: boolean;
  activeService: boolean;
  records: ConsentRecord[];
  mode: 'OFF' | 'SHADOW' | 'ENFORCE';
}

export interface ConsentPage {
  content: ConsentRecord[];
  totalElements: number;
  page: number;
  size: number;
  mode: 'OFF' | 'SHADOW' | 'ENFORCE';
}

export interface ConsentShadowSummary {
  since: string;
  mode: 'OFF' | 'SHADOW' | 'ENFORCE';
  wouldRefuseNoConsent: number;
  byVerdict: Record<string, number>;
  byVerdictAndCategory: Record<string, Record<string, number>>;
}

export type ComplaintStatus = 'OPEN' | 'EVIDENCE_SENT' | 'CLOSED_VALID' | 'CLOSED_INVALID';
export const COMPLAINT_STATUSES: ComplaintStatus[] = ['OPEN', 'EVIDENCE_SENT', 'CLOSED_VALID', 'CLOSED_INVALID'];

export interface Complaint {
  id: number;
  organizationId: string;
  complaintRef: string;
  receivedAt: string;
  /** XXXXXX + last four. */
  complainant: string | null;
  cli: string;
  category: string | null;
  circle: string | null;
  status: ComplaintStatus;
  evidenceDueAt: string;
  evidenceOverdue: boolean;
  notes: string | null;
}

export interface CliRisk {
  cli: string;
  organizationId: string;
  complaints: number;
  uniqueComplainants: number;
  aiFlagged: boolean;
  level: 'OK' | 'WARNING' | 'WOULD_SUSPEND';
  detail: string;
}

export interface RiskView {
  organizationId: string;
  thresholds: { windowDays: number; uniqueWithFlag: number; uniqueAlone: number; kycFlaggedClis: number };
  complaints: number;
  aiFlaggedClis: number;
  kycReverification: boolean;
  senderBarRisk: boolean;
  detail: string;
  clis: CliRisk[];
  enforcement: string;
}

export interface ComplianceAlert {
  id: number;
  organizationId: string;
  kind: 'CLI_WOULD_SUSPEND' | 'CLI_WARNING' | 'ORG_KYC_REVERIFICATION';
  subject: string;
  windowKey: string;
  detail: string | null;
  channel: string;
  status: string;
  createdAt: string;
}

/** A 200 with success:false carries its reason in `message`. */
const body = <T,>(res: any): ApiResponse<T> => {
  const b = res?.data ?? {};
  if (b.success === false) return { success: false, error: b.message || b.error || 'Request refused' };
  return b as ApiResponse<T>;
};

const call = async <T,>(p: Promise<any>, msg: string): Promise<ApiResponse<T>> => {
  try { return body<T>(await p); }
  catch (e: any) { return apiError(e, msg) as ApiResponse<T>; }
};

const BASE = '/v1/compliance';

export const traiComplianceService = {
  // ── 1. categories ──
  categories: () => call<CategoryDefaults>(axiosInstance.get(`${BASE}/categories`), 'Failed to load call categories'),
  setOrgCategory: (category: A2pCategory | null) =>
    call<any>(axiosInstance.put(`${BASE}/categories/org`, { category }), 'Failed to save the organisation default'),
  setCampaignCategory: (campaignId: string, category: A2pCategory | null) =>
    call<any>(axiosInstance.put(`${BASE}/categories/campaigns/${encodeURIComponent(campaignId)}`, { category }),
      'Failed to save the campaign category'),
  setAgentCategory: (agentId: string | number, category: A2pCategory | null) =>
    call<any>(axiosInstance.put(`${BASE}/categories/agents/${encodeURIComponent(String(agentId))}`, { category }),
      'Failed to save the agent category'),

  // ── 2. numbers ── (list is a bare array)
  numbers: () => call<DeclaredNumber[]>(axiosInstance.get(`${BASE}/numbers`), 'Failed to load numbers'),
  report: (days = 30) =>
    call<DeclarationReport>(axiosInstance.get(`${BASE}/numbers/report`, { params: { days } }), 'Failed to load the report'),
  setDeclaration: (numberId: number, d: { declaredAt?: string; reference?: string; category?: A2pCategory | ''; regime?: string }) =>
    call<DeclaredNumber>(axiosInstance.put(`${BASE}/numbers/${numberId}/declaration`, d), 'Failed to save the declaration'),
  clearDeclaration: (numberId: number) =>
    call<DeclaredNumber>(axiosInstance.delete(`${BASE}/numbers/${numberId}/declaration`), 'Failed to clear the declaration'),
  importDeclarations: (csv: string, dryRun: boolean) =>
    call<ImportResult>(axiosInstance.post(`${BASE}/numbers/declarations/import`, { csv }, { params: { dryRun } }),
      'Failed to import declarations'),

  // ── 3. consent ── (list is data.content)
  consents: (page = 0, size = 50) =>
    call<ConsentPage>(axiosInstance.get(`${BASE}/consents`, { params: { page, size } }), 'Failed to load consent records'),
  lookupConsent: (phone: string) =>
    call<ConsentLookup>(axiosInstance.get(`${BASE}/consents/lookup`, { params: { phone } }), 'Failed to look up consent'),
  recordConsent: (r: { phone: string; source: ConsentSource; scope?: ConsentScope; capturedAt?: string; evidenceRef?: string; expiresAt?: string }) =>
    call<ConsentRecord>(axiosInstance.post(`${BASE}/consents`, r), 'Failed to record consent'),
  importConsents: (csv: string, dryRun: boolean) =>
    call<ImportResult>(axiosInstance.post(`${BASE}/consents/bulk`, { csv }, { params: { dryRun } }), 'Failed to import consent'),
  revokeConsent: (id: number, reason?: string) =>
    call<ConsentRecord>(axiosInstance.post(`${BASE}/consents/${id}/revoke`, { reason }), 'Failed to revoke consent'),
  revokeNumber: (phone: string, reason?: string) =>
    call<{ number: string; revoked: number }>(axiosInstance.post(`${BASE}/consents/revoke-number`, { phone, reason }),
      'Failed to revoke consent'),
  consentShadow: (days = 30) =>
    call<ConsentShadowSummary>(axiosInstance.get(`${BASE}/consents/shadow-summary`, { params: { days } }),
      'Failed to load the shadow summary'),

  // ── 4. complaints ── (list is data.content)
  complaints: (all = false, page = 0, size = 50) =>
    call<{ content: Complaint[]; totalElements: number }>(
      axiosInstance.get(`${BASE}/complaints`, { params: { all, page, size } }), 'Failed to load complaints'),
  upsertComplaint: (c: Record<string, any>) =>
    call<Complaint>(axiosInstance.post(`${BASE}/complaints`, c), 'Failed to save the complaint'),
  updateComplaint: (id: number, c: Record<string, any>) =>
    call<Complaint>(axiosInstance.put(`${BASE}/complaints/${id}`, c), 'Failed to update the complaint'),
  importComplaints: (csv: string, dryRun: boolean) =>
    call<ImportResult>(axiosInstance.post(`${BASE}/complaints/import`, { csv }, { params: { dryRun } }),
      'Failed to import complaints'),
  risk: () => call<RiskView>(axiosInstance.get(`${BASE}/complaints/risk`), 'Failed to load the risk view'),
  setCliFlag: (cli: string, f: { aiFlagged: boolean; flaggedAt?: string; flagRef?: string; notes?: string }) =>
    call<any>(axiosInstance.put(`${BASE}/complaints/cli-flags/${encodeURIComponent(cli)}`, f), 'Failed to save the AI flag'),
  alerts: (all = false) =>
    call<{ content: ComplianceAlert[]; totalElements: number }>(
      axiosInstance.get(`${BASE}/complaints/alerts`, { params: { all } }), 'Failed to load alerts'),
};
